import {CommonModule} from '@angular/common';
import {Component, computed, effect, inject, signal} from '@angular/core';
import {FormControl} from '@angular/forms';
import {MatMenuModule} from '@angular/material/menu';
import {Router, RouterModule} from '@angular/router';
import {ClinicListItem} from '@domain/clinic/clinic-list';
import {AdminAuthService} from '@infrastructure/admin/auth/auth.service';
import {ClinicService} from '@infrastructure/admin/clinics/clinic.service';
import {AgendaService} from '@infrastructure/clinician/agenda/agenda.service';
import {ClinicianService} from '@infrastructure/clinician/clinician.service';
import {TranslateModule} from '@ngx-translate/core';
import {CustomButtonComponent} from '@shared/custom-button/custom-button.component';
import {DropDownComponent, DropdownOption} from '@shared/dropdown/dropdown.component';
import {AngularSvgIconModule} from 'angular-svg-icon';
import {AdminService} from 'src/infrastructure/admin/admin.service';
import {GenericAuthService} from 'src/infrastructure/generic-auth/generic-auth.service';
import {OwnerService} from 'src/infrastructure/owner/owner.service';
import {SearchInputComponent} from '../search-input/search-input.component';
import {NotificationCenterComponent} from './notifications/notification-center.component';
import {TopBarService} from './top-bar.service';

@Component({
    selector: 'app-top-bar',
    templateUrl: './top-bar.component.html',
    styleUrl: './top-bar.component.scss',
    standalone: true,
    imports: [
        AngularSvgIconModule,
        TranslateModule,
        SearchInputComponent,
        RouterModule,
        CommonModule,
        CustomButtonComponent,
        DropDownComponent,
        MatMenuModule,
        NotificationCenterComponent
    ]
})
export class TopBarComponent
{
    private readonly genericAuthService = inject(GenericAuthService);

    private readonly ownerService = inject(OwnerService);

    private readonly adminService = inject(AdminService);

    private readonly clinicianAgendaService = inject(AgendaService);

    private readonly adminAuthService = inject(AdminAuthService);

    private readonly clinicianService = inject(ClinicianService);

    private readonly topBarService = inject(TopBarService);

    private readonly clinicsService = inject(ClinicService);

    private readonly router = inject(Router);

    private readonly clinics = computed(() => this.clinicsService.clinics());

    public readonly user = computed(() =>
    {
        if (this.genericAuthService.isOwner())
        {
            return this.ownerService.owner();
        }
        else if (this.genericAuthService.isAdmin())
        {
            return this.adminService.admin();
        }
        else if (this.genericAuthService.isClinician())
        {
            return this.clinicianService.clinician();
        }
    });

    public readonly isAdminOrClinician = computed(() => this.genericAuthService.isAdmin() || this.genericAuthService.isClinician());

    public readonly isClinician = computed(() => this.genericAuthService.isClinician());

    public readonly admin = computed(() => this.adminService.admin());

    public readonly changeProfileIcon = computed(() => `assets/icons/${this.isClinician() ? 'hospital' : 'badge'}.svg`);

    public readonly changeProfileLabel = computed(() => `GENERAL.PROFILE_TOGGLER.${this.isClinician() ? 'ADMIN' : 'CLINICIAN'}`);

    public readonly title = computed(() => this.topBarService.getTitle());

    public readonly showFilters = computed(() => this.topBarService.getShowFilterArea());

    public readonly showInviteButton = computed(() => this.topBarService.getShowInviteButton());

    public readonly showTopBar = computed(() => this.topBarService.getShowTopBar());

    public readonly showClinicsDropdown = computed(() => this.topBarService.getShowClinicsDropdown());

    public readonly showAddClinicButton = computed(() => this.topBarService.getShowAddClinicButton());

    public readonly showAddCampaignButton = computed(() => this.topBarService.getShowAddCampaignButton());

    public readonly showAddSpecialityButton = computed(() => this.topBarService.getShowAddSpecialityButton());

    public readonly clinicOptions = computed(() =>
    {
        return this.clinics().map((clinic: ClinicListItem) => ({
            value: clinic.id,
            label: clinic.name
        }));
    });

    public clinicControl = new FormControl(null);

    public readonly userTypeLabel = computed(() =>
    {
        if (this.genericAuthService.isOwner())
        {
            return 'GENERAL.ROLE.OWNER';
        }
        else if (this.genericAuthService.isAdmin())
        {
            return 'GENERAL.ROLE.ADMIN';
        }
        else if (this.genericAuthService.isClinician())
        {
            return 'GENERAL.ROLE.CLINICIAN';
        }
    });

    public userName = computed(() => `${this.user()?.firstName ?? ''} ${this.user()?.lastName ?? ''}`);

    public picture = computed(() => this.user()?.picture ?? 'assets/images/avatar_placeholder.webp');

    public openMenuOptions = signal<boolean>(false);

    public constructor()
    {
        if (this.genericAuthService.isAdmin())
        {
            this.clinicsService.getClinics();
        }

        effect(() =>
        {
            if (this.clinics().length > 0)
            {
                this.clinicControl.setValue(this.clinics()[0].id);
            }
        }, {allowSignalWrites: true});

        this.clinicControl.valueChanges.subscribe((clinicId: string) =>
        {
            this.topBarService.setSelectedClinic(this.clinicOptions().find((clinic: DropdownOption) => clinic.value === clinicId));
        });

        this.topBarService.onSelectedClinicChanged().subscribe((clinic: DropdownOption) =>
        {
            if (this.clinicControl.value !== clinic.value)
            {
                this.clinicControl.setValue(clinic.value);
            }
        });
    }

    public logout(): void
    {
        this.genericAuthService.logout();
    }

    public menuOptionsTrigger(): void
    {
        this.openMenuOptions.set(!this.openMenuOptions());
    }

    public buttonClicked(): void
    {
        this.topBarService.emitClick();
    }

    public changeProfile(): void
    {
        this.openMenuOptions.set(false);

        this.genericAuthService.toggleProfileAndFetchInformation();
    }

    public getDashboardLink(): string
    {
        return `/dashboard/${this.genericAuthService.loggedUserType()}/profile`;
    }
}
